// Shared profiler dump schema. Both client (src/profile/NetcodeProfiler.ts)
// and server (server/src/profile/ServerNetcodeProfiler.ts) write into this
// shape. The exporter concatenates both halves into a single JSON file.
//
// Layout convention:
//  * Dense per-tick metrics use COLUMN-ORIENTED arrays (one `number[]` per
//    field, parallel-indexed). Easy to slice by tick, easy to plot, compact
//    in JSON.
//  * Sparse events use ROW-ORIENTED arrays (each entry is an object with
//    the event's fields). Used for reconciles, impacts, input sends, etc.
//  * All wall-clock timestamps are `wallTimeMs = performance.now() - t0`
//    where `t0` is captured at `start()`. Client and server each use their
//    own monotonic clock; cross-referencing is done via SERVER TICK, which
//    is the authoritative synchronisation axis.
//  * All bone poses are flattened: `bonePoses[tickIdx * 63 + boneIdx * 7 + comp]`
//    with 9 bones × [px, py, pz, qx, qy, qz, qw]. Parallel to the tick axis
//    in `ticks.serverTick` (server) / `simTicks.clientTick` (client).

export const PROFILER_VERSION = 1;
export const BONES_PER_CHAR = 9;
export const FLOATS_PER_BONE = 7;

/** One row per rAF callback invocation on the client. */
export interface RafFrames {
  idx: number[];
  wallTimeMs: number[];
  realDtMs: number[];
  simSubstepsThisFrame: number[];
  accumulatorPreMs: number[];
  accumulatorPostMs: number[];
  alpha: number[];
  frameDurationMs: number[];
}

/** One row per client-side fixed-step tick (transport.tick). */
export interface ClientSimTicks {
  clientTick: number[];
  wallTimeMs: number[];
  dtMs: number[];
  tickDurationMs: number[];
  stepDurationMs: number[];
  synthDurationMs: number[];
  syncLocalScalarDurationMs: number[];
  capturePredictedDurationMs: number[];
  reconcileDurationMs: number[];
  /** lastProcessedInputTick observed at the start of this tick (from newest
   *  server patch). Useful to compute ack lag vs clientTick. */
  ackTickObserved: number[];
  // Local char state AFTER world.step (so metrics reflect the pose the mesh
  // will render).
  localPelvisX: number[];
  localPelvisY: number[];
  localPelvisZ: number[];
  localFacingYaw: number[];
  localLinvelX: number[];
  localLinvelY: number[];
  localLinvelZ: number[];
  localAngvelY: number[];
  localHp: number[];
  localKo: number[];
  localStamina: number[];
  localStunRemaining: number[];
  localGrabbing: number[];
  localBeingGrabbed: number[];
  // Input consumed this tick (stamped to clientTick).
  inputMoveX: number[];
  inputMoveZ: number[];
  inputAimYaw: number[];
  inputButtons: number[];
}

/** Local-character PD / animation internal state per client tick.
 *  Drives footstep+gait+hip-plant behaviour. Expected to be stable when
 *  standing idle; violent oscillation here correlates with visible wobble. */
export interface ClientPdStateLocal {
  clientTick: number[];
  targetYaw: number[];
  /** NaN when null (not planted). */
  hipPlantX: number[];
  hipPlantZ: number[];
  gaitPhase: number[];
  gaitActivity: number[];
  jumpKickActive: number[];
  punchCooldownL: number[];
  punchCooldownR: number[];
  swingPhaseL: number[];
  swingPhaseR: number[];
}

/** Remote-character per-tick slice. One entry per remote charId (not the
 *  requesting player). Lets the analyst see how the client's own sim of
 *  each remote evolved under forwarded inputs + per-patch snap. */
export interface ClientRemoteCharSlice {
  clientTick: number[];
  /** Server tick associated with this client tick, if known. */
  serverTickApprox: number[];
  pelvisX: number[];
  pelvisY: number[];
  pelvisZ: number[];
  facingYaw: number[];
  /** Queue depth BEFORE consume on this tick. */
  inputQueueDepthBefore: number[];
  /** 1 = dequeued a fresh input, 0 = extrapolated last consumed, -1 = no input ever. */
  inputSource: number[];
  /** Server tick the dequeued input was stamped with (-1 if not consumed). */
  inputConsumedTick: number[];
  inputMoveX: number[];
  inputMoveZ: number[];
  inputAimYaw: number[];
  inputButtons: number[];
}

/** One row per schema patch arrival on the client. */
export interface ClientPatches {
  wallTimeMs: number[];
  serverTick: number[];
  /** Ms since previous patch arrival. Variance in this is a primary cadence
   *  signal. */
  interArrivalMs: number[];
  estimatedBytes: number[];
  playerCount: number[];
}

/** One row per bones binary message arrival on the client. */
export interface ClientBonesMessages {
  wallTimeMs: number[];
  /** Associated server tick if a patch arrived first, else -1. */
  serverTickApprox: number[];
  sizeBytes: number[];
  interArrivalMs: number[];
  charsIncluded: number[];
}

/** One row per reconcileFromServer invocation. Captures what was decided
 *  and why. Even 'noop' rows are valuable — they show the steady-state
 *  drift pattern. */
export interface ClientReconcileEvent {
  wallTimeMs: number;
  clientTick: number;
  serverAckTick: number;
  ageTicks: number;
  pelvisDriftM: number;
  /** 9 entries, worst-to-best. Populated only when we can compute predicted. */
  perBoneDriftM: number[];
  action: 'noop' | 'rollback' | 'seed' | 'snap-stale' | 'snap-no-ack' | 'skip-grab-coupled' | 'skip-no-bones' | 'skip-no-predicted';
  replayedTicks: number;
  /** Only populated for 'rollback'. Pre/post pelvis from the physics body. */
  prePelvisX?: number;
  prePelvisY?: number;
  prePelvisZ?: number;
  postPelvisX?: number;
  postPelvisY?: number;
  postPelvisZ?: number;
  /** Per-bone pos delta magnitude after replay (post - pre). 9 entries if present. */
  postReplayPerBoneDeltaM?: number[];
}

export interface ClientInputSend {
  wallTimeMs: number;
  /** The newest InputFrame.tick bundled. */
  clientTick: number;
  bundleSize: number;
  estimatedBytes: number;
}

export interface ClientImpactEvent {
  wallTimeMs: number;
  fromCharId: number;
  toCharId: number;
  dmg: number;
  posX: number;
  posY: number;
  posZ: number;
}

export interface ClientFootstepEvent {
  wallTimeMs: number;
  charId: number;
  foot: 'L' | 'R';
  posX: number;
  posY: number;
  posZ: number;
}

export interface ClientSwingEvent {
  wallTimeMs: number;
  charId: number;
  side: 'L' | 'R';
}

export interface ClientHealthSample {
  wallTimeMs: number;
  fps: number;
  tickPerSec: number;
  ping: number;
  interpDelayMs: number;
  rollbackPerSec: number;
  avgDriftM: number;
  worstBoneDriftM: number;
  remoteInputQueueDepth: Record<number, number>;
}

export interface ClientProfile {
  rafFrames: RafFrames;
  simTicks: ClientSimTicks;
  pdStateLocal: ClientPdStateLocal;
  /** 9 × 7 floats per tick, flat. Parallel to simTicks.clientTick. */
  localBonePoses: number[];
  /** keyed by remote charId. */
  remoteChars: Record<number, ClientRemoteCharSlice>;
  patches: ClientPatches;
  bonesMessages: ClientBonesMessages;
  reconciles: ClientReconcileEvent[];
  inputSends: ClientInputSend[];
  impacts: ClientImpactEvent[];
  footsteps: ClientFootstepEvent[];
  swings: ClientSwingEvent[];
  health: ClientHealthSample[];
  /** Number of rollbackCount increments at start/end. Lets us compute
   *  rollbacks-during-recording without walking the event log. */
  rollbackCountStart: number;
  rollbackCountEnd: number;
}

/** Per server tick, globally. */
export interface ServerTicks {
  serverTick: number[];
  wallTimeMs: number[];
  /** Actual ms since previous tick (setInterval cadence jitter measure). */
  tickIntervalActualMs: number[];
  stepDurationMs: number[];
  syncStateDurationMs: number[];
  broadcastDurationMs: number[];
  totalDurationMs: number[];
  /** How many connected (non-AI) clients at this tick. */
  humanClientCount: number[];
}

/** Per server tick PER CHAR. */
export interface ServerCharSlice {
  isLocalAtStart: boolean;
  isNpcAtStart: boolean;
  /** Parallel arrays. */
  serverTick: number[];
  pelvisX: number[];
  pelvisY: number[];
  pelvisZ: number[];
  facingYaw: number[];
  linvelX: number[];
  linvelY: number[];
  linvelZ: number[];
  angvelY: number[];
  hp: number[];
  ko: number[];
  stamina: number[];
  stunRemaining: number[];
  grabbing: number[];
  beingGrabbed: number[];
  /** Depth of this char's inputQueue BEFORE the server consumed this tick's
   *  input. High = client is ahead; 0 = underrun (extrapolation). */
  inputQueueDepthBefore: number[];
  /** 1 = dequeued a fresh input from the queue; 0 = extrapolated the last
   *  consumed; -1 = no input ever (AI/NPC path). */
  inputSource: number[];
  /** Client tick of the consumed frame (-1 if extrapolated/NPC). */
  inputConsumedClientTick: number[];
  /** Actual input values applied this tick (human OR AI). */
  inputAppliedMoveX: number[];
  inputAppliedMoveZ: number[];
  inputAppliedAimYaw: number[];
  inputAppliedButtons: number[];
  /** Server-authoritative PD state. */
  pdTargetYaw: number[];
  /** NaN if not planted. */
  pdHipPlantX: number[];
  pdHipPlantZ: number[];
  pdGaitPhase: number[];
  pdGaitActivity: number[];
  /** ackTick at end of this tick — lastProcessedInputTick for the slot. */
  ackTickEndOfTick: number[];
}

export interface ServerInputReceived {
  wallTimeMs: number;
  fromCharId: number;
  sessionId: string;
  bundleSize: number;
  oldestClientTick: number;
  newestClientTick: number;
  addedToQueue: number;
  droppedStale: number;
  queueSizeAfter: number;
}

export interface ServerBroadcasts {
  serverTick: number[];
  wallTimeMs: number[];
  bonesBytes: number[];
  forwardInputsFrames: number[];
  patchFlushDurationMs: number[];
}

export interface ServerImpactEvent {
  wallTimeMs: number;
  serverTick: number;
  fromCharId: number;
  toCharId: number;
  dmg: number;
}

export interface ServerAckJump {
  wallTimeMs: number;
  serverTick: number;
  charId: number;
  oldAck: number;
  newAck: number;
}

export interface ServerProfile {
  ticks: ServerTicks;
  /** keyed by charId (0..3). */
  perChar: Record<number, ServerCharSlice>;
  /** keyed by charId — flat bone poses parallel to `perChar[id].serverTick`. */
  bonePoses: Record<number, number[]>;
  inputsReceived: ServerInputReceived[];
  broadcasts: ServerBroadcasts;
  impacts: ServerImpactEvent[];
  ackJumps: ServerAckJump[];
}

export interface DumpMeta {
  schemaVersion: number;
  sessionId: string;
  /** Date.now() at client start() call. */
  recordedAtMs: number;
  durationMs: number;
  clientTickCount: number;
  serverTickCount: number;
  /** The char id that requested the recording ("the player"). */
  localCharId: number;
  clientVersion: string;
  serverVersion: string;
  tickHz: number;
  mapId: string;
  screenRefreshHz: number;
  /** Player ping in ms at export time (measured RTT / 2-ish per Colyseus). */
  pingMsAtExport: number;
  /** Roster snapshot at export — human-readable mapping from charId to role. */
  roster: Array<{ charId: number; name: string; team: 'red' | 'blue'; isNpc: boolean; isLocal: boolean }>;
}

/** The exported JSON shape. */
export interface CombinedDump {
  meta: DumpMeta;
  client: ClientProfile;
  server: ServerProfile | { error: string };
}

// --------------------------------------------------------------------------
// Constructors / helpers

export function makeEmptyClientProfile(): ClientProfile {
  return {
    rafFrames: {
      idx: [], wallTimeMs: [], realDtMs: [], simSubstepsThisFrame: [],
      accumulatorPreMs: [], accumulatorPostMs: [], alpha: [], frameDurationMs: []
    },
    simTicks: {
      clientTick: [], wallTimeMs: [], dtMs: [],
      tickDurationMs: [], stepDurationMs: [], synthDurationMs: [],
      syncLocalScalarDurationMs: [], capturePredictedDurationMs: [], reconcileDurationMs: [],
      ackTickObserved: [],
      localPelvisX: [], localPelvisY: [], localPelvisZ: [],
      localFacingYaw: [],
      localLinvelX: [], localLinvelY: [], localLinvelZ: [],
      localAngvelY: [],
      localHp: [], localKo: [], localStamina: [], localStunRemaining: [],
      localGrabbing: [], localBeingGrabbed: [],
      inputMoveX: [], inputMoveZ: [], inputAimYaw: [], inputButtons: []
    },
    pdStateLocal: {
      clientTick: [], targetYaw: [],
      hipPlantX: [], hipPlantZ: [],
      gaitPhase: [], gaitActivity: [],
      jumpKickActive: [],
      punchCooldownL: [], punchCooldownR: [],
      swingPhaseL: [], swingPhaseR: []
    },
    localBonePoses: [],
    remoteChars: {},
    patches: {
      wallTimeMs: [], serverTick: [], interArrivalMs: [], estimatedBytes: [], playerCount: []
    },
    bonesMessages: {
      wallTimeMs: [], serverTickApprox: [], sizeBytes: [], interArrivalMs: [], charsIncluded: []
    },
    reconciles: [],
    inputSends: [],
    impacts: [],
    footsteps: [],
    swings: [],
    health: [],
    rollbackCountStart: 0,
    rollbackCountEnd: 0
  };
}

export function makeEmptyRemoteSlice(): ClientRemoteCharSlice {
  return {
    clientTick: [], serverTickApprox: [],
    pelvisX: [], pelvisY: [], pelvisZ: [],
    facingYaw: [],
    inputQueueDepthBefore: [],
    inputSource: [], inputConsumedTick: [],
    inputMoveX: [], inputMoveZ: [], inputAimYaw: [], inputButtons: []
  };
}

export function makeEmptyServerProfile(): ServerProfile {
  return {
    ticks: {
      serverTick: [], wallTimeMs: [], tickIntervalActualMs: [],
      stepDurationMs: [], syncStateDurationMs: [], broadcastDurationMs: [],
      totalDurationMs: [], humanClientCount: []
    },
    perChar: {},
    bonePoses: {},
    inputsReceived: [],
    broadcasts: {
      serverTick: [], wallTimeMs: [], bonesBytes: [],
      forwardInputsFrames: [], patchFlushDurationMs: []
    },
    impacts: [],
    ackJumps: []
  };
}

export function makeEmptyServerCharSlice(isLocal: boolean, isNpc: boolean): ServerCharSlice {
  return {
    isLocalAtStart: isLocal,
    isNpcAtStart: isNpc,
    serverTick: [],
    pelvisX: [], pelvisY: [], pelvisZ: [],
    facingYaw: [],
    linvelX: [], linvelY: [], linvelZ: [],
    angvelY: [],
    hp: [], ko: [], stamina: [], stunRemaining: [],
    grabbing: [], beingGrabbed: [],
    inputQueueDepthBefore: [],
    inputSource: [], inputConsumedClientTick: [],
    inputAppliedMoveX: [], inputAppliedMoveZ: [],
    inputAppliedAimYaw: [], inputAppliedButtons: [],
    pdTargetYaw: [],
    pdHipPlantX: [], pdHipPlantZ: [],
    pdGaitPhase: [], pdGaitActivity: [],
    ackTickEndOfTick: []
  };
}
